import { Popup, DatePickerView, Button, PickerView } from 'antd-mobile';
import { DatePickerFilter } from 'antd-mobile/es/components/date-picker';
import { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import classnames from 'classnames';
import { i18nTranslate } from '@didi/i18n-plugin';

import { RangePickerChoiceType, PickerType } from '@/config';
import { useIsMobile } from '@/utils';
import { ReactComponent as ArrowDown } from '@/assets/images/downArrow.svg';
import { ReactComponent as CloseCircle } from '@/assets/images/close_circle.svg';
import closeIcon from '@/assets/images/close.png';
import swapRight from '@/assets/images/swap_right.png';

import styles from './index.module.less';

import type { PickerValue } from 'antd-mobile/es/components/picker-view';

type PickerDate = Date & {
  tillNow?: boolean;
};

const MobileRangePicker = (props: any) => {
  const { presets, showTime, format, allowClear, value = [], onChange, maxRange = 180 } = props;
  const isMobile = useIsMobile();
  const [visible, setVisible] = useState(false);
  const [choiceType, setChoiceType] = useState<RangePickerChoiceType>(RangePickerChoiceType.Date);
  const [pickerType, setPickerType] = useState<PickerType>(PickerType.StartDate);
  const [showDelete, setShowDelete] = useState<Boolean>(false);
  const [shortcutValue, setShortcutValue] = useState<PickerValue[]>([]);
  const [datePickerValue, setDatePickerValue] = useState<Dayjs[]>([
    dayjs().startOf('day'),
    dayjs().endOf('day')
  ]); // 默认选中当天

  useEffect(() => {
    if (visible) {
      if (value.length > 0) {
        // 有值，默认选中时间段
        setChoiceType(RangePickerChoiceType.Date);
        setDatePickerValue(value);
      } else if (value.length === 0 && presets) {
        // 默认选中快捷选项第一个
        setChoiceType(RangePickerChoiceType.Shortcut);
        setShortcutValue([presets[0]?.value]);
        setDatePickerValue(presets[0]?.value);
      } else if (value.length === 0 && !presets) {
        // 默认选中当天
        setChoiceType(RangePickerChoiceType.Date);
      }
    }
  }, [visible]);

  const monthText = (month: number) => {
    switch (month) {
      case 1:
        return i18nTranslate('Internal_Tools_2_KVNS_Tidn') || '一月';
      case 2:
        return i18nTranslate('Internal_Tools_2_ShWE_pqHC') || '二月';
      case 3:
        return i18nTranslate('Internal_Tools_2_DgKa_MWcm') || '三月';
      case 4:
        return i18nTranslate('Internal_Tools_2_MTMo_BaMI') || '四月';
      case 5:
        return i18nTranslate('Internal_Tools_2_VPzK_SlSn') || '五月';
      case 6:
        return i18nTranslate('Internal_Tools_2_ANMs_tYaS') || '六月';
      case 7:
        return i18nTranslate('Internal_Tools_2_lYFD_lpHi') || '七月';
      case 8:
        return i18nTranslate('Internal_Tools_2_HapJ_nsTk') || '八月';
      case 9:
        return i18nTranslate('Internal_Tools_2_tnYP_zmjj') || '九月';
      case 10:
        return i18nTranslate('Internal_Tools_2_kjSs_qYzP') || '十月';
      case 11:
        return i18nTranslate('Internal_Tools_2_swod_ShaV') || '十一月';
      case 12:
        return i18nTranslate('Internal_Tools_2_jfyZ_sGtA') || '十二月';
      default:
        return month;
    }
  };

  const labelRenderer = (type: string, data: number) => {
    switch (type) {
      case 'month':
        return monthText(data);
      default:
        return data;
    }
  };

  const formatLabel = (i: number) => (i < 10 ? `0${i}` : `${i}`);

  const timeColumns = () => {
    const hour = [];
    const minute = [];
    const second = [];

    for (let i = 0; i < 60; i += 1) {
      const label = formatLabel(i);

      if (i < 24) {
        hour.push({ label, value: i });
      }

      minute.push({ label, value: i });
      second.push({ label, value: i });
    }

    return [hour, minute, second];
  };

  const getValueText = (date: any) => {
    if (date) {
      return dayjs(date).format(format);
    }
    return '';
  };

  const getDateValueText = (date: any) => {
    const dateFormat = format.split(' ')[0];

    return dayjs(date).format(dateFormat);
  };

  const getTimeValueText = (date: any) => {
    const timeFormat = format.split(' ')[1];

    return dayjs(date).format(timeFormat);
  };

  const getPickTime = (type: PickerType) => {
    if (type === PickerType.StartTime) {
      const start = dayjs(datePickerValue?.[0]).format('HH:mm:ss');

      return start.split(':').map(Number);
    }
    const end = dayjs(datePickerValue?.[1]).format('HH:mm:ss');

    return end.split(':').map(Number);
  };

  const enableDateMonthAndYear = (date: Date) => {
    if (pickerType === PickerType.EndDate) {
      const isAfter = dayjs(date).isAfter(dayjs(datePickerValue[0]));

      if (!isAfter) return true;
      const isMaxRange = dayjs(date).diff(dayjs(datePickerValue[0]), 'day') <= maxRange;

      return isAfter && isMaxRange;
    }

    return true;
  };
  const enableDateDay = (date: Date) => {
    if (pickerType === PickerType.EndDate) {
      const isAfter = dayjs(date).isAfter(dayjs(datePickerValue[0]));
      const isMaxRange = dayjs(date).diff(dayjs(datePickerValue[0]), 'day') <= maxRange;

      return isAfter && isMaxRange;
    }

    return true;
  };

  const dateFilter: DatePickerFilter = {
    year: (val, { date }) => {
      return enableDateMonthAndYear(date);
    },
    month: (val, { date }) => {
      return enableDateMonthAndYear(date);
    },
    day: (val, { date }) => {
      return enableDateDay(date);
    }
  };

  return (
    <div>
      <div
        className={classnames(
          'px-[12px] border-[1px] border-[#DADBE0] rounded-[4px] flex items-center justify-between',
          {
            'h-[40px]': isMobile,
            'h-[32px]': !isMobile
          }
        )}
        onMouseEnter={() => setShowDelete(true)}
        onMouseLeave={() => setShowDelete(false)}
      >
        <div className='flex w-full items-center'>
          <div
            className='flex w-1/2'
            onClick={() => {
              setVisible(true);
              setPickerType(PickerType.StartDate);
            }}
          >
            {getValueText(value?.[0]) || (
              <span className='text-gray-6'>
                {i18nTranslate('Internal_Tools_2_VunY_IMzD') || '开始时间'}
              </span>
            )}
          </div>
          <div className='px-[8px] flex items-center'>
            <img src={swapRight} width={16} height={3} />
          </div>

          <div
            onClick={() => {
              setVisible(true);
              setPickerType(PickerType.EndDate);
            }}
          >
            {getValueText(value?.[1]) || (
              <span className='text-gray-6'>
                {i18nTranslate('Internal_Tools_2_eBtC_jSgY') || '结束时间'}
              </span>
            )}
          </div>
        </div>
        {showDelete && allowClear ? (
          <CloseCircle
            width={14}
            height={14}
            onClick={() => {
              onChange?.([]);
            }}
          />
        ) : (
          <ArrowDown width={14} height={14} style={{ color: '#C2C4CC' }} />
        )}
      </div>
      <Popup
        visible={visible}
        onMaskClick={() => {
          setVisible(false);
        }}
        bodyStyle={{
          borderTopLeftRadius: '18px',
          borderTopRightRadius: '18px',
          minHeight: choiceType === RangePickerChoiceType.Shortcut ? '360px' : '500px'
        }}
      >
        {
          <div className='flex flex-col'>
            {/* title */}
            <div className='flex ml-[18px] mr-[23px] h-[64px] items-center justify-between'>
              <div className='text-xl font-DiDiSansPro-Bold text-[#333]'>
                {i18nTranslate('Internal_Tools_2_uYtG_HGQZ')}
              </div>
              <img
                src={closeIcon}
                width={14}
                height={14}
                className='ml-[8px] shrink-0'
                onClick={() => {
                  setVisible(false);
                }}
              />
            </div>
            {/* 快捷选项 + 时间段 */}
            {presets && (
              <div className='flex h-[52px] leading-[52px] text-base font-DiDiSansPro-Medium text-[#CCC]'>
                <div
                  className='w-1/2 flex justify-center'
                  onClick={() => setChoiceType(RangePickerChoiceType.Shortcut)}
                >
                  <span
                    className={
                      choiceType === RangePickerChoiceType.Shortcut ? styles.underline : ''
                    }
                  >
                    {i18nTranslate('Internal_Tools_2_AzpT_hWwx')}
                  </span>
                </div>
                <div
                  className='w-1/2 flex justify-center'
                  onClick={() => setChoiceType(RangePickerChoiceType.Date)}
                >
                  <span
                    className={choiceType === RangePickerChoiceType.Date ? styles.underline : ''}
                  >
                    {i18nTranslate('Internal_Tools_2_nbUx_lwXP')}
                  </span>
                </div>
              </div>
            )}
            <>
              {choiceType === RangePickerChoiceType.Shortcut && presets ? (
                <>
                  {/* 快捷选项 */}
                  <PickerView
                    className={styles.pickerview}
                    value={shortcutValue}
                    columns={[
                      presets?.map((item: any, index: number) => ({
                        label: item.label,
                        value: index
                      }))
                    ]}
                    style={{
                      '--height': '150px',
                      '--item-height': '48px',
                      '--item-font-size': '20px'
                    }}
                    onChange={val => {
                      setShortcutValue(val);
                      setDatePickerValue(presets[val[0] || 0].value);
                    }}
                  />
                </>
              ) : (
                <>
                  {/* 时间面板 */}
                  <div className='flex bg-[#F5F5F7] h-[82px] text-[#999]'>
                    <div className='flex flex-col justify-center items-center w-[100px] text-base font-DiDiSans-Pro-Regular text-[#999]'>
                      <span
                        className={classnames('leading-[40px]', {
                          'font-DiDiSansPro-Bold text-[#333]': [
                            PickerType.StartDate,
                            PickerType.StartTime
                          ].includes(pickerType)
                        })}
                      >
                        {i18nTranslate('Internal_Tools_2_hhOc_JJAW')}
                      </span>
                      <span className='leading-[1px]'>-</span>
                      <span
                        className={classnames('leading-[40px]', {
                          'font-DiDiSansPro-Bold text-[#333]': [
                            PickerType.EndDate,
                            PickerType.EndTime
                          ].includes(pickerType)
                        })}
                      >
                        {i18nTranslate('Internal_Tools_2_BecI_qaZY')}
                      </span>
                    </div>
                    <div className='w-full text-base font-DiDiSansPro-Bold text-[#999]'>
                      <div className='flex leading-[34px] mt-[5px]'>
                        <div
                          className={classnames(
                            'flex justify-center mr-[4px] h-[34px] rounded-[6px] bg-gray-1',
                            {
                              'text-[#333]': pickerType === PickerType.StartDate,
                              'w-1/2': showTime,
                              'w-full': !showTime
                            }
                          )}
                          onClick={() => {
                            setPickerType(PickerType.StartDate);
                          }}
                        >
                          {getDateValueText(datePickerValue?.[0])}
                        </div>
                        {showTime && (
                          <div
                            className={classnames(
                              'flex justify-center mr-[4px] w-1/2 h-[34px] rounded-[6px] bg-gray-1',
                              {
                                'text-[#333]': pickerType === PickerType.StartTime
                              }
                            )}
                            onClick={() => {
                              setPickerType(PickerType.StartTime);
                            }}
                          >
                            {getTimeValueText(datePickerValue?.[0])}
                          </div>
                        )}
                      </div>
                      <div className='flex leading-[34px] mt-[4px]'>
                        <div
                          className={classnames(
                            'flex justify-center mr-[4px] h-[34px] rounded-[6px] bg-gray-1',
                            {
                              'text-[#333]': pickerType === PickerType.EndDate,
                              'w-1/2': showTime,
                              'w-full': !showTime
                            }
                          )}
                          onClick={() => {
                            setPickerType(PickerType.EndDate);
                          }}
                        >
                          {getDateValueText(datePickerValue?.[1])}
                        </div>
                        {showTime && (
                          <div
                            className={classnames(
                              'flex justify-center mr-[4px] w-1/2 h-[34px] rounded-[6px] bg-gray-1',
                              {
                                'text-[#333]': pickerType === PickerType.EndTime
                              }
                            )}
                            onClick={() => {
                              setPickerType(PickerType.EndTime);
                            }}
                          >
                            {getTimeValueText(datePickerValue?.[1])}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* 时间picker */}
                  <div>
                    {[PickerType.StartDate, PickerType.EndDate].includes(pickerType) ? (
                      <>
                        <div className='mt-[30px] mb-[10px] flex h-[24px] text-sm text-[#999]'>
                          <span className='w-1/3 flex justify-center'>
                            {i18nTranslate('Internal_Tools_2_qMwU_rvhb') || '年'}
                          </span>
                          <span className='w-1/3 flex justify-center'>
                            {i18nTranslate('Internal_Tools_2_aeNy_MYVh') || '月'}
                          </span>
                          <span className='w-1/3 flex justify-center'>
                            {i18nTranslate('Internal_Tools_2_ThRd_xnHT') || '日'}
                          </span>
                        </div>
                        <DatePickerView
                          className={styles.pickerview}
                          value={
                            pickerType === PickerType.StartDate
                              ? datePickerValue?.[0]?.toDate()
                              : datePickerValue?.[1]?.toDate()
                          }
                          renderLabel={labelRenderer}
                          onChange={(pickerDate: PickerDate) => {
                            let dayjsDate = dayjs(pickerDate);

                            if (showTime) {
                              if (pickerType === PickerType.StartDate) {
                                dayjsDate = dayjsDate
                                  .hour(datePickerValue[0].hour())
                                  .minute(datePickerValue[0].minute())
                                  .second(datePickerValue[0].second());
                              } else {
                                dayjsDate = dayjsDate
                                  .hour(datePickerValue[1].hour())
                                  .minute(datePickerValue[1].minute())
                                  .second(datePickerValue[1].second());
                              }
                            }
                            let newDatePickerValue = datePickerValue;

                            if (pickerType === PickerType.StartDate) {
                              if (dayjs(dayjsDate).isAfter(dayjs(datePickerValue[1]))) {
                                // 如果开始时间晚于结束时间，结束时间调整为开始时间的后一天
                                newDatePickerValue = [dayjsDate, dayjsDate.add(1, 'day')];
                              } else if (
                                // 如果开始时间与结束时间相差大于最大范围，结束时间调整为开始时间的后maxRange天
                                dayjs(datePickerValue[1]).diff(dayjs(dayjsDate), 'day') > maxRange
                              ) {
                                newDatePickerValue = [dayjsDate, dayjsDate.add(maxRange, 'day')];
                              } else {
                                newDatePickerValue = [dayjsDate, datePickerValue[1]];
                              }
                            }
                            if (pickerType === PickerType.EndDate) {
                              if (dayjs(datePickerValue[0]).isAfter(dayjs(dayjsDate))) {
                                // 如果结束时间晚于开始时间，开始时间调整为结束时间的前一天
                                newDatePickerValue = [dayjsDate.subtract(1, 'day'), dayjsDate];
                              } else if (
                                // 如果开始时间与结束时间相差大于最大范围，开始时间调整为结束时间的前maxRange天
                                dayjs(dayjsDate).diff(dayjs(datePickerValue[0]), 'day') > maxRange
                              ) {
                                newDatePickerValue = [
                                  dayjsDate.subtract(maxRange, 'day'),
                                  dayjsDate
                                ];
                              } else {
                                newDatePickerValue = [datePickerValue[0], dayjsDate];
                              }
                            }

                            setDatePickerValue(newDatePickerValue);
                          }}
                          precision='day'
                          filter={dateFilter}
                          style={{
                            '--height': '150px',
                            '--item-height': '48px',
                            '--item-font-size': '20px'
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <div className='mt-[30px] mb-[10px] flex h-[24px] text-sm text-[#999]'>
                          <span className='w-1/3 flex justify-center'>
                            {i18nTranslate('Internal_Tools_2_guyK_dNWP') || '时'}
                          </span>
                          <span className='w-1/3 flex justify-center'>
                            {i18nTranslate('Internal_Tools_2_YBkJ_wHPL') || '分'}
                          </span>
                          <span className='w-1/3 flex justify-center'>
                            {i18nTranslate('Internal_Tools_2_NgEj_cmVx') || '秒'}
                          </span>
                        </div>
                        <PickerView
                          className={styles.pickerview}
                          value={getPickTime(pickerType)}
                          columns={timeColumns}
                          onChange={(val: PickerValue[]) => {
                            let dayjsDate = null;

                            if (pickerType === PickerType.StartTime) {
                              dayjsDate = datePickerValue[0]
                                .hour(val[0] as number)
                                .minute(val[1] as number)
                                .second(val[2] as number);
                            } else {
                              dayjsDate = datePickerValue[1]
                                .hour(val[0] as number)
                                .minute(val[1] as number)
                                .second(val[2] as number);
                            }
                            let newDatePickerValue =
                              pickerType === PickerType.StartTime
                                ? [dayjsDate, datePickerValue[1]]
                                : [datePickerValue[0], dayjsDate];

                            // 如果开始时间晚于结束时间，交换它们
                            if (dayjs(newDatePickerValue[0]).isAfter(newDatePickerValue[1])) {
                              newDatePickerValue = [newDatePickerValue[1], newDatePickerValue[0]];
                              setPickerType(
                                pickerType === PickerType.StartTime
                                  ? PickerType.EndTime
                                  : PickerType.StartTime
                              );
                            }

                            setDatePickerValue(newDatePickerValue);
                          }}
                          style={{
                            '--height': '150px',
                            '--item-height': '48px',
                            '--item-font-size': '20px'
                          }}
                        />
                      </>
                    )}
                  </div>
                </>
              )}
            </>

            {/* 确定button */}
            <div style={{ position: 'fixed', bottom: 16, left: 16, right: 16 }}>
              <Button
                color='primary'
                size='large'
                className='w-full h-[54px] bg-[#FF8040] rounded-[6px] border-[#FF8040] text-lg font-DiDiSansPro-Bold text-gray-1'
                onClick={() => {
                  if (choiceType === RangePickerChoiceType.Shortcut) {
                    onChange?.(presets[shortcutValue[0] || 0].value);
                  } else {
                    onChange?.(datePickerValue);
                  }

                  setVisible(false);
                }}
              >
                {i18nTranslate('Internal_Tools_B_maAi_LDaH')}
              </Button>
            </div>
          </div>
        }
      </Popup>
    </div>
  );
};

export default MobileRangePicker;
